/**
 * EL-198 — o diálogo "Relatório comparativo": QUAIS cenários e PARA QUAL objetivo (lote de
 * 09/10/2026, item 7). Aberto pelo botão EL-196 (rodapé do seletor de cenários e atalho na
 * Comparação, ao lado do "Comparar operações").
 *
 * POR QUE UM COMPONENTE NOVO: `formulario()` (EL-187) só tem campos de texto, e o menu ⋮
 * (EL-186) é de ações, não de escolha múltipla.
 *
 * AS REGRAS DA ESCOLHA (decisões de 09/10/2026):
 *   · só os cenários ATIVOS do MESMO empreendimento, lidos de novo do banco a cada abertura
 *     (a lista que a tela tem em mãos é da montagem — um colega pode ter criado um depois);
 *   · de 2 a 5;
 *   · cenário NUNCA publicado não pode ser escolhido (o relatório usa a versão publicada de
 *     cada um) — aparece desligado, com o motivo escrito;
 *   · o objetivo é escolhido aqui, na hora: os mesmos do modo Guiado. O último usado fica
 *     lembrado NESTE navegador (conforto, não dado — try/catch, e sem ele nada quebra);
 *   · se EU tenho alteração não publicada num dos marcados, o diálogo avisa ANTES (e o
 *     relatório avisa de novo, por escrito).
 *
 * ABRIR EM ABA NOVA SEM PERDER O QUE FOI DIGITADO: a aba nova começa do zero e não enxerga a
 * edição que ainda está na fila do rascunho desta aba. Por isso `gravarAgora()` ANTES de
 * mandar a aba ao endereço — e a aba é aberta em branco no PRÓPRIO clique (senão o
 * bloqueador de janelas a barra, porque o endereço só sai depois de uma espera).
 */
import { h, limpar } from '../dom';
import { icone } from '../icones';
import { marcarIds } from '../ui/ids';
import { avisar } from '../ui/aviso';
import { CONSTANTES } from './frase';
import { enderecoDoRelatorio } from './endereco';
import { OBJETIVOS, objetivoPorChave } from './textos';
import type { ChaveObjetivo } from './textos';
import { seloCenario } from '../cenarios-regras';
import { listarCenarios } from '../../../../packages/dados/src/repositorios';
import type { CenarioResumo } from '../../../../packages/dados/src/repositorios';
import { cenariosComRascunho, gravarAgora } from '../../../../packages/dados/src/rascunho';

export interface OpcoesDialogoRelatorio {
  empreendimentoId: string;
  /** Já marcados ao abrir (o que está marcado na Comparação). Vazio = todos os publicados. */
  preselecionados: readonly string[];
  /** Quem edita tem rascunho: só para essa pessoa há o que gravar e o que avisar. */
  editavel: boolean;
  /** Admin Hype: as etiquetas do "Mostrar IDs". */
  marcarIds: boolean;
}

const CHAVE_OBJETIVO = 'fincast.relatorio.objetivo';

function ultimoObjetivo(): ChaveObjetivo | null {
  try {
    return objetivoPorChave(localStorage.getItem(CHAVE_OBJETIVO))?.k ?? null;
  } catch {
    return null;
  }
}

function lembrarObjetivo(k: ChaveObjetivo): void {
  try {
    localStorage.setItem(CHAVE_OBJETIVO, k);
  } catch {
    /* armazenamento bloqueado: a próxima abertura não traz o objetivo marcado */
  }
}

/** O diálogo aberto agora (um por vez) — a tela o fecha ao sair. */
let fecharAberto: (() => void) | null = null;

export function fecharDialogoRelatorio(): void {
  fecharAberto?.();
}

/**
 * Abre a aba do relatório. Em branco no clique (é o gesto do usuário que libera a janela),
 * e só vai ao endereço depois de gravar o rascunho desta aba.
 */
async function abrirAba(url: string, gravar: boolean): Promise<void> {
  const aba = window.open('', '_blank');
  if (aba !== null) {
    try {
      aba.document.title = 'Relatório comparativo — Fincast';
      aba.document.body.textContent = 'Preparando o relatório…';
    } catch {
      /* aba de outra origem ou já navegando: só esperar */
    }
  }
  if (gravar) {
    const s = await gravarAgora();
    if (s.pendente) {
      avisar('Não foi possível gravar o que você digitou agora. O relatório usa a versão publicada de cada cenário, então ele não depende disso.', 'aviso');
    }
  }
  if (aba !== null) {
    aba.opener = null;
    aba.location.replace(url);
  } else {
    avisar('O navegador bloqueou a aba do relatório. Permita janelas do Fincast e tente de novo.', 'erro');
  }
}

export function abrirDialogoRelatorio(o: OpcoesDialogoRelatorio): void {
  fecharDialogoRelatorio();
  let fechado = false;
  const fechar = (): void => {
    if (fechado) return;
    fechado = true;
    fecharAberto = null;
    document.removeEventListener('keydown', aoTeclar, true);
    fundo.remove();
  };
  function aoTeclar(e: KeyboardEvent): void {
    /* a tela pode ter sido desmontada por baixo (navegação): sem o nó, o ouvinte sai */
    if (!fundo.isConnected) { fechar(); return; }
    if (e.key === 'Escape') { e.stopPropagation(); fechar(); }
  }

  const lista = h('div', { class: 'reld-lista', role: 'group', 'aria-label': 'Cenários' }, h('div', { class: 'reld-carregando muted' }, 'Lendo os cenários…'));
  const objetivos = h('div', { class: 'reld-objs', role: 'radiogroup', 'aria-label': 'Objetivo' });
  const aviso = h('div', { class: 'reld-aviso', role: 'note', hidden: true });
  const estado = h('div', { class: 'reld-estado', 'aria-live': 'polite' });
  const gerar = h('button', { class: 'btn pri', type: 'button', disabled: true, 'data-acao': 'gerar' },
    h('span', { html: icone.relatorio(15) }), 'Gerar relatório') as HTMLButtonElement;

  let cenarios: CenarioResumo[] = [];
  let comRascunho = new Set<string>();
  const marcados = new Set<string>();
  let objetivo: ChaveObjetivo | null = ultimoObjetivo();

  const caixa = h(
    'div',
    { class: 'modal reld', 'data-el': 'EL-198', role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'reld-tit' },
    h('div', { class: 'mh' },
      h('span', { class: 'reld-ic', html: icone.relatorio(18) }),
      h('div', {}, h('h3', { id: 'reld-tit' }, 'Relatório comparativo'),
        h('div', { class: 'd' }, 'Escolha de 2 a 5 cenários deste empreendimento e o objetivo da comparação. O relatório abre em outra aba, pronto para imprimir ou salvar em PDF.')),
      h('button', { class: 'iconbtn', style: 'margin-left:auto', type: 'button', title: 'Fechar', onclick: fechar }, '✕')),
    h('div', { class: 'reld-bd' },
      h('div', { class: 'reld-col' }, h('h4', {}, 'Cenários'), lista),
      h('div', { class: 'reld-col' }, h('h4', {}, 'Objetivo'), objetivos)),
    aviso,
    h('div', { class: 'reld-ft' }, estado,
      h('button', { class: 'btn out', type: 'button', onclick: fechar }, 'Cancelar'), gerar),
  );
  const fundo = h('div', { class: 'ov show', onclick: (e: Event) => { if (e.target === fundo) fechar(); } }, caixa);

  function atualizar(): void {
    const n = marcados.size;
    const falta: string[] = [];
    if (n < CONSTANTES.minCenarios) falta.push(n === 0 ? 'marque pelo menos 2 cenários' : 'marque mais um cenário');
    if (objetivo === null) falta.push('escolha o objetivo');
    estado.textContent = `${n} de ${CONSTANTES.maxCenarios} marcados${falta.length > 0 ? ' · ' + falta.join(' e ') : ''}`;
    gerar.disabled = falta.length > 0;
    for (const el of lista.querySelectorAll<HTMLInputElement>('input[type=checkbox]')) {
      if (el.dataset.publicado !== '1') continue;
      el.disabled = !el.checked && n >= CONSTANTES.maxCenarios;
    }
    const meus = cenarios.filter((c) => marcados.has(c.id) && comRascunho.has(c.id)).map((c) => `"${c.nome}"`);
    aviso.hidden = meus.length === 0;
    limpar(aviso);
    if (meus.length > 0) {
      aviso.appendChild(h('span', { html: icone.alerta(15) }));
      aviso.appendChild(h('span', {}, `Você tem alterações não publicadas em ${meus.join(', ')}. O relatório usa a última versão PUBLICADA de cada cenário — publique antes se quiser que elas entrem.`));
    }
  }

  function desenharObjetivos(): void {
    limpar(objetivos);
    for (const ob of OBJETIVOS) {
      const id = `reld-obj-${ob.k}`;
      const radio = h('input', {
        type: 'radio', name: 'reld-objetivo', id, value: ob.k, checked: objetivo === ob.k,
        onchange: () => { objetivo = ob.k; atualizar(); },
      });
      objetivos.appendChild(h('label', { class: 'reld-obj', for: id }, radio,
        h('span', { class: 'reld-obj-tx' }, h('span', { class: 'reld-obj-t' }, ob.t), h('span', { class: 'reld-obj-d' }, ob.d))));
    }
  }

  function desenharCenarios(): void {
    limpar(lista);
    const pre = new Set(o.preselecionados);
    const publicados = cenarios.filter((c) => c.versaoPublicada > 0);
    const iniciais = pre.size > 0 ? publicados.filter((c) => pre.has(c.id)) : publicados;
    for (const c of iniciais.slice(0, CONSTANTES.maxCenarios)) marcados.add(c.id);
    for (const c of cenarios) {
      const publicado = c.versaoPublicada > 0;
      const id = `reld-cen-${c.id}`;
      const selo = seloCenario(c.versaoPublicada, comRascunho.has(c.id));
      const caixaMarcar = h('input', {
        type: 'checkbox', id, value: c.id, checked: marcados.has(c.id), disabled: !publicado,
        'data-publicado': publicado ? '1' : '0',
        onchange: (e: Event) => {
          const alvo = e.target as HTMLInputElement;
          if (alvo.checked) marcados.add(c.id); else marcados.delete(c.id);
          atualizar();
        },
      });
      lista.appendChild(h('label', { class: `reld-cen${publicado ? '' : ' off'}`, for: id, 'data-cenario-id': c.id },
        caixaMarcar,
        h('span', { class: 'reld-cen-tx' },
          h('span', { class: 'reld-cen-nm', title: c.nome }, c.nome),
          h('span', { class: `badge ${selo.tom} reld-selo` }, selo.texto),
          publicado ? null : h('span', { class: 'reld-motivo' }, 'Publique este cenário para poder incluí-lo: o relatório usa a versão publicada de cada um.'))));
    }
    if (cenarios.length === 0) lista.appendChild(h('div', { class: 'muted' }, 'Este empreendimento não tem cenários ativos.'));
    else if (publicados.length < CONSTANTES.minCenarios) {
      lista.appendChild(h('div', { class: 'reld-motivo', role: 'note' },
        'Para comparar, é preciso ter pelo menos dois cenários publicados neste empreendimento.'));
    }
  }

  gerar.addEventListener('click', () => {
    if (gerar.disabled || objetivo === null) return;
    /* na ordem da lista (a do seletor), não na ordem dos cliques */
    const ids = cenarios.filter((c) => marcados.has(c.id)).map((c) => c.id);
    lembrarObjetivo(objetivo);
    const url = enderecoDoRelatorio(ids, objetivo);
    fechar();
    void abrirAba(url, o.editavel);
  });

  desenharObjetivos();
  atualizar();
  document.body.appendChild(fundo);
  document.addEventListener('keydown', aoTeclar, true);
  fecharAberto = fechar;
  /* `marcarIds` olha os DESCENDENTES da raiz, e o único `data-el` do diálogo (EL-198) é o da
     própria caixa: `marcarIds(caixa)` não fazia nada. O fundo já está no <body> e tem a caixa
     como descendente — sem embrulho, que a tiraria de dentro dele. */
  if (o.marcarIds) marcarIds(fundo);

  void (async () => {
    try {
      cenarios = await listarCenarios(o.empreendimentoId);
      if (o.editavel) {
        try {
          comRascunho = await cenariosComRascunho(cenarios.map((c) => ({ id: c.id, versao: c.versao })));
        } catch {
          comRascunho = new Set();
        }
      }
      if (fechado) return;
      desenharCenarios();
      atualizar();
      caixa.setAttribute('data-pronto', '1');
      (lista.querySelector<HTMLInputElement>('input:not(:disabled)') ?? gerar).focus();
    } catch (e) {
      if (fechado) return;
      limpar(lista);
      lista.appendChild(h('div', { class: 'reld-erro' }, e instanceof Error ? e.message : String(e)));
    }
  })();
}
